import React from 'react';
import Layout from '../../../components/Layout';
import {
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line
} from 'recharts';
import { Download, TrendingUp } from 'lucide-react';

const SalesReport = () => {
    // Initial empty state (Real data to be fetched from API later)
    // eslint-disable-next-line no-unused-vars
    const [data, setData] = React.useState([
        { name: 'Jan', sales: 4000, packages: 2400 },
        { name: 'Feb', sales: 3000, packages: 1398 },
        { name: 'Mar', sales: 2000, packages: 9800 },
        { name: 'Apr', sales: 2780, packages: 3908 },
        { name: 'May', sales: 1890, packages: 4800 },
        { name: 'Jun', sales: 2390, packages: 3800 },
    ]);

    React.useEffect(() => {
        // TODO: Fetch sales reports from API
    }, []);

    return (
        <Layout>
            <div className="space-y-8 p-1 sm:p-2">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                            <TrendingUp className="text-blue-600" size={32} />
                            Sales Report
                        </h1>
                        <p className="text-gray-500 font-medium tracking-tight">Monthly performance and product distribution analytics</p>
                    </div>
                    <button className="w-full sm:w-auto px-6 py-3 bg-white border border-gray-100 text-gray-700 rounded-2xl hover:bg-gray-50 font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 shadow-sm transition-all active:scale-95">
                        <Download size={18} /> Export detailed report
                    </button>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 relative overflow-hidden group">
                        <div className="absolute -right-8 -top-8 w-32 h-32 bg-blue-50 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700" />
                        <div className="relative z-10 flex items-center gap-4">
                            <div className="p-4 bg-blue-600 text-white rounded-2xl shadow-lg shadow-blue-100">
                                <TrendingUp size={24} />
                            </div>
                            <div>
                                <p className="text-gray-400 text-[10px] font-black uppercase tracking-[0.2em] mb-1">Total Revenue</p>
                                <h3 className="text-2xl font-black text-gray-900 leading-none">₹ 0</h3>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 relative overflow-hidden group">
                        <div className="absolute -right-8 -top-8 w-32 h-32 bg-green-50 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700" />
                        <div className="relative z-10 flex items-center gap-4">
                            <div className="p-4 bg-green-600 text-white rounded-2xl shadow-lg shadow-green-100">
                                <TrendingUp size={24} />
                            </div>
                            <div>
                                <p className="text-gray-400 text-[10px] font-black uppercase tracking-[0.2em] mb-1">Packages Sold</p>
                                <h3 className="text-2xl font-black text-gray-900 leading-none">0</h3>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-gray-100 relative overflow-hidden group">
                        <div className="absolute -right-8 -top-8 w-32 h-32 bg-indigo-50 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700" />
                        <div className="relative z-10 flex items-center gap-4">
                            <div className="p-4 bg-indigo-600 text-white rounded-2xl shadow-lg shadow-indigo-100">
                                <TrendingUp size={24} />
                            </div>
                            <div>
                                <p className="text-gray-400 text-[10px] font-black uppercase tracking-[0.2em] mb-1">Avg. Order Value</p>
                                <h3 className="text-2xl font-black text-gray-900 leading-none">₹ 0</h3>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Charts */}
                <div className="bg-white p-6 sm:p-8 rounded-[2.5rem] shadow-sm border border-gray-100 h-[500px] sm:h-[450px]">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h3 className="text-lg font-black text-gray-900 uppercase tracking-tight">Revenue Growth</h3>
                            <p className="text-xs text-gray-400 font-bold tracking-tight">Monthly sales performance visualization</p>
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height="80%">
                        <LineChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis
                                dataKey="name"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: '#94a3b8', fontSize: 12, fontWeight: 700 }}
                                dy={10}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: '#94a3b8', fontSize: 12, fontWeight: 700 }}
                            />
                            <Tooltip
                                contentStyle={{ borderRadius: '1.25rem', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)', padding: '1rem' }}
                                itemStyle={{ fontWeight: 900, fontSize: '0.875rem' }}
                            />
                            <Legend wrapperStyle={{ paddingTop: '2rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '10px' }} />
                            <Line
                                type="monotone"
                                dataKey="sales"
                                stroke="#2563eb"
                                strokeWidth={4}
                                dot={{ r: 6, fill: '#2563eb', strokeWidth: 2, stroke: '#fff' }}
                                activeDot={{ r: 8, strokeWidth: 0 }}
                                animationDuration={2000}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </Layout>
    );
};

export default SalesReport;
